import { datadogLogs } from '@datadog/browser-logs';
// ✅ CORREÇÃO: Remover import direto do supabase para evitar dependência circular
// import { supabase } from '@/integrations/supabase/client';

// Salvando comentário só pra atualizar versão
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

const LOG_LEVEL_NAMES = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR'
};

// CORREÇÃO: Usar a mesma lógica de detecção de ambiente do main.tsx
const getEnvironment = () => {
  const hostname = window.location.hostname;
  
  // Production environments (highest priority)
  if (hostname === 'interlaced.app') {
    return 'production';
  }
  
  // Preview environments (staging URLs)
  if (hostname === 'preview--interlaced.lovable.app') {
    return 'preview';
  }
  
  // Preview environments with ID prefix
  if (hostname.includes('id-preview--') && hostname.includes('.lovable.app')) {
    return 'preview';
  }
  
  // Preview environments with ID and commit prefix
  if (hostname.includes('id-preview-') && hostname.includes('--') && hostname.includes('.lovable.app')) {
    return 'preview';
  }
  
  // Development environment (Lovable editor interface)
  if (hostname.includes('.lovableproject.com')) {
    return 'development';
  }
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  // Any other .lovable.app domains (fallback for preview)
  if (hostname.includes('.lovable.app')) {
    return 'preview';
  }
  
  // Default fallback
  return 'unknown';
};

/**
 * DebugLogger - Centralized logging for the application
 * 
 * Features:
 * - Environment-aware logging (only logs in development/preview)
 * - Automatic Datadog integration
 * - User context enrichment
 * - Configurable log levels
 * - Automatic enabling on Lovable preview domains
 * 
 * ⚠️ IMPORTANT: DO NOT USE debugLogger IN SUPABASE EDGE FUNCTIONS ⚠️
 * 
 * Edge Functions run in a Deno environment and should use native console methods:
 * - Use console.log() for general logging
 * - Use console.error() for errors
 * - Use console.warn() for warnings
 * 
 * debugLogger is designed for frontend/browser environments only and will
 * not work correctly in Edge Functions.
 * 
 * ✅ USE debugLogger FOR:
 * - Business logic logging (payments, plans, subscriptions)
 * - User actions tracking
 * - API calls and responses
 * - Error reporting
 * - Telemetry infrastructure (use debugLogger.debug with 'telemetry' category)
 * - Analytics tracking (use debugLogger.debug with 'analytics' category)
 * 
 * 📋 RECOMMENDED CATEGORIES:
 * - 'telemetry': OpenTelemetry spans, context management
 * - 'analytics': Google Analytics events
 * - 'asaas': Asaas payment provider operations
 * - 'stripe': Stripe payment provider operations
 * - 'plans': Plan upgrades and changes
 * - 'auth': Authentication and user management
 * - 'chat': Messaging and conversations
 * - 'feed': Feed and profile browsing
 * 
 * 📊 LOG LEVELS:
 * - DEBUG: Detailed telemetry and environment info (production: disabled)
 * - INFO: Normal operations (production: disabled)
 * - WARN: Recoverable issues (production: enabled)
 * - ERROR: Critical errors (production: enabled)
 * 
 * Usage (Frontend only):
 *   debugLogger.debug('telemetry', 'PageSpan started:', routeName)
 *   debugLogger.info('asaas', 'Creating checkout session:', data)
 *   debugLogger.error('stripe', 'Payment failed:', error)
 */

// User context enrichment removed for performance - handled at app initialization if needed

class DebugLogger {
  private static instance: DebugLogger;
  private currentLogLevel: LogLevel | null = null;
  private autoEnabledOnLovable: boolean = false;

  static getInstance(): DebugLogger {
    if (!DebugLogger.instance) {
      DebugLogger.instance = new DebugLogger();
    }
    return DebugLogger.instance;
  }

  private isTestEnv(): boolean {
    try {
      const inNode = typeof process !== 'undefined' && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test');
      const inVite = typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE === 'test';
      const inBrowser = typeof window !== 'undefined' && (
        (window as any).__VITEST_BROWSER__ === true ||
        (navigator && (navigator as any).webdriver === true) ||
        (window as any).Cypress
      );
      return inNode || inVite || inBrowser;
    } catch {
      return false;
    }
  }

  constructor() {
    // ✅ Exit immediately in test environment - no initialization at all
    if (this.isTestEnv()) {
      (window as any).__DEBUG_LOGGER_INITIALIZED__ = true;
      return;
    }
    // ✅ FASE 2: Reset guard on HMR
    if ((import.meta as any).hot) {
      (import.meta as any).hot.dispose(() => {
        delete (window as any).__DEBUG_LOGGER_INITIALIZED__;
        debugLogger.debug('hmr', 'Logger guards cleared for hot reload');
      });
    }
    
    // GUARD GLOBAL: Prevenir múltiplas inicializações mesmo com HMR
    if ((window as any).__DEBUG_LOGGER_INITIALIZED__) {
      console.log('[DebugLogger] Already initialized (global guard), skipping auto-enable check...');
      return;
    }
    
    // Auto-enable debug on Lovable domains and specific preview URL
    this.checkAndAutoEnableOnLovable();
    
    // Marcar como inicializado
    (window as any).__DEBUG_LOGGER_INITIALIZED__ = true;
  }

  private checkAndAutoEnableOnLovable(): void {
    // ✅ Skip in test environment
    if (this.isTestEnv()) return;
    
    try {
      if (typeof window !== 'undefined') {
        const environment = getEnvironment();
        const hostname = window.location.hostname;
        
        // Auto-enable for development, local, AND specifically for preview--interlaced.lovable.app
        // AND for the specific hostname 880c84c1-f69e-42a5-9aee-855237d7d223.lovableproject.com
        const isLovableDomain = environment === 'development' || environment === 'local';
        const isSpecificPreview = hostname === 'preview--interlaced.lovable.app';
        const isSpecificDevHost = hostname.includes('880c84c1-f69e-42a5-9aee-855237d7d223.lovableproject.com');
        
        if ((isLovableDomain || isSpecificPreview || isSpecificDevHost) && !this.autoEnabledOnLovable) {
          localStorage.setItem('debug_mode', 'true');
          localStorage.setItem('debugLogLevel', LogLevel.DEBUG.toString());
          this.autoEnabledOnLovable = true;
          
          // Apenas logar se debug mode estiver ativo
          if (this._shouldLogInit()) {
            console.log(`[DEBUG][init] Auto-enabled debug mode on environment: ${environment}, hostname: ${hostname}`);
          }
        }
      }
    } catch (error) {
      // Silently fail - don't break anything if this fails
    }
  }

  private getCurrentLogLevel(): LogLevel {
    // ✅ Return ERROR level in test environment without logging
    if (this.isTestEnv()) return LogLevel.ERROR;
    
    try {
      // Check if we need to refresh our cached level
      const storedLevel = localStorage.getItem('debugLogLevel');
      if (storedLevel && Object.values(LogLevel).includes(Number(storedLevel))) {
        const newLevel = Number(storedLevel) as LogLevel;
        
        // Update cached level if it changed
        if (this.currentLogLevel !== newLevel) {
          this.currentLogLevel = newLevel;
          if (this.isLocalDevelopment() && this._shouldLogInit()) {
            console.log(`[DEBUG][init] Log level changed to: ${LOG_LEVEL_NAMES[newLevel]}`);
          }
        }
        
        return newLevel;
      }
      
      // Default to WARN if no level set
      const defaultLevel = LogLevel.WARN;
      if (this.currentLogLevel !== defaultLevel) {
        this.currentLogLevel = defaultLevel;
      }
      return defaultLevel;
    } catch {
      return LogLevel.WARN;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    try {
      // In test environment, only log critical errors
      if (this.isTestEnv()) {
        return level >= LogLevel.ERROR;
      }
      
      return level >= this.getCurrentLogLevel();
    } catch (err) {
      console.warn('Erro ao determinar nível de log:', err);
      return true;
    }
  }

  private _shouldLogInit(): boolean {
    // ✅ Never log init in test environment
    if (this.isTestEnv()) return false;
    
    // Apenas logar init se debug mode estiver explicitamente ativo
    return this.getCurrentLogLevel() === LogLevel.DEBUG;
  }

  private isLocalDevelopment(): boolean {
    // ✅ Return false in test environment
    if (this.isTestEnv()) return false;
    
    try {
      const environment = getEnvironment();
      const hostname = window.location.hostname;
      
      return environment === 'local' || 
             environment === 'development' ||
             hostname === 'preview--interlaced.lovable.app' ||
             hostname.includes('880c84c1-f69e-42a5-9aee-855237d7d223.lovableproject.com') ||
             localStorage.getItem('debug_mode') === 'true';
    } catch {
      return false;
    }
  }

  // CORREÇÃO: Permitir envio de todos os logs para Datadog na URL específica E no hostname específico
  private shouldSendToDatadog(level: LogLevel): boolean {
    try {
      const environment = getEnvironment();
      const hostname = window.location.hostname;
      
      // Exceção específica para preview--interlaced.lovable.app E para o hostname específico: enviar TODOS os logs
      if (hostname === 'preview--interlaced.lovable.app' || hostname.includes('880c84c1-f69e-42a5-9aee-855237d7d223.lovableproject.com')) {
        return true; // Enviar DEBUG, INFO, WARN, ERROR
      }
      
      // Em outros ambientes de desenvolvimento/preview, só enviar WARN e ERROR
      if (environment === 'local' || environment === 'development' || environment === 'preview') {
        return level >= LogLevel.WARN;
      }
      
      // Em produção, enviar todos os logs
      return true;
    } catch {
      // Em caso de erro, assumir produção e enviar todos
      return true;
    }
  }

  log(...args: any[]): void {
    this._logInternal(LogLevel.INFO, ...args);
  }

  info(...args: any[]): void {
    this._logInternal(LogLevel.INFO, ...args);
  }

  warn(...args: any[]): void {
    this._logInternal(LogLevel.WARN, ...args);
  }

  error(...args: any[]): void {
    this._logInternal(LogLevel.ERROR, ...args);
  }

  debug(...args: any[]): void {
    this._logInternal(LogLevel.DEBUG, ...args);
  }

  private getCallerFile(): string {
    try {
      const stack = new Error().stack?.split('\n');
      if (stack && stack.length >= 4) {
        const line = stack[3];
        const match = line.match(/\(([^)]+):\d+:\d+\)/) || line.match(/at ([^ ]+):\d+:\d+/);
        if (match && match[1]) {
          return match[1].split('/').pop() || 'unknown';
        }
      }
    } catch {
      // ignore
    }
    return 'unknown';
  }

  private parseArgs(args: any[]): { module: string; message: string; rest: any[] } {
    if (args.length === 0) {
      return { module: 'App', message: '', rest: [] };
    }

    const first = args[0];
    if (typeof first === 'string' && first.startsWith('[') && first.includes(']')) {
      const closing = first.indexOf(']');
      const module = first.substring(1, closing);
      const message = first.substring(closing + 1).trim();
      return { module, message, rest: args.slice(1) };
    }

    if (args.length >= 2 && typeof args[0] === 'string' && typeof args[1] === 'string') {
      return { module: args[0], message: args[1], rest: args.slice(2) };
    }

    if (typeof args[0] === 'string') {
      return { module: 'App', message: args[0], rest: args.slice(1) };
    }

    return { module: 'App', message: '', rest: args };
  }

  /**
   * Sanitize data to prevent File/Blob objects from being sent to Datadog
   */
  private sanitizeData(data: any): any {
    if (!data) return data;
    
    // Remove File/Blob objects (prevents base64 overflow in Datadog)
    if (data instanceof File || data instanceof Blob) {
      return { 
        type: data instanceof File ? 'File' : 'Blob', 
        name: (data as File).name || 'unknown', 
        size: data.size || 0 
      };
    }
    
    // Remove HTMLElement instances (prevents DOM serialization overflow)
    if (typeof HTMLElement !== 'undefined' && data instanceof HTMLElement) {
      return {
        type: 'HTMLElement',
        tagName: data.tagName,
        id: data.id || undefined,
        className: data.className || undefined
      };
    }
    
    // Truncate data URLs (prevents base64 image overflow in logs)
    if (typeof data === 'string' && data.startsWith('data:image/')) {
      const commaIndex = data.indexOf(',');
      if (commaIndex > 0) {
        return data.substring(0, commaIndex + 100) + '...[truncated]';
      }
      return data.substring(0, 100) + '...[truncated]';
    }
    
    // Sanitize arrays
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }
    
    // Sanitize objects
    if (typeof data === 'object') {
      const sanitized: any = {};
      for (const key in data) {
        if (data[key] instanceof File || data[key] instanceof Blob) {
          sanitized[key] = { 
            type: data[key] instanceof File ? 'File' : 'Blob', 
            name: (data[key] as File).name || 'unknown', 
            size: data[key].size || 0 
          };
        } else if (typeof HTMLElement !== 'undefined' && data[key] instanceof HTMLElement) {
          sanitized[key] = {
            type: 'HTMLElement',
            tagName: data[key].tagName,
            id: data[key].id || undefined,
            className: data[key].className || undefined
          };
        } else if (typeof data[key] === 'string' && data[key].startsWith('data:image/')) {
          const commaIndex = data[key].indexOf(',');
          if (commaIndex > 0) {
            sanitized[key] = data[key].substring(0, commaIndex + 100) + '...[truncated]';
          } else {
            sanitized[key] = data[key].substring(0, 100) + '...[truncated]';
          }
        } else if (typeof data[key] === 'object' && data[key] !== null) {
          sanitized[key] = this.sanitizeData(data[key]);
        } else {
          sanitized[key] = data[key];
        }
      }
      return sanitized;
    }
    
    return data;
  }

  private _logInternal(level: LogLevel, ...args: any[]): void {
    // Exit early in test environment - no operations at all
    if (this.isTestEnv()) {
      return;
    }
    
    if (!this.shouldLog(level)) {
      return;
    }

    // User context enrichment removed for performance
    // If needed, handle at app initialization in main.tsx

    const { module, message, rest } = this.parseArgs(args);
    const levelName = LOG_LEVEL_NAMES[level] || 'INFO';
    const callerFile = this.getCallerFile();
    const baseMessage = `[${levelName}][${module}][${callerFile}] ${message}`;

    // Send to Datadog if appropriate for the environment
    if (this.shouldSendToDatadog(level)) {
      try {
        // Use simplified logging with sanitized data
        datadogLogs.logger.log(baseMessage, { 
          level: levelName, 
          data: this.sanitizeData(rest)  // ✅ Sanitize before sending
        });
      } catch (err) {
        console.warn('Erro ao enviar log para o Datadog:', err, 'Mensagem original:', baseMessage);
      }
    }

    // Log local em desenvolvimento
    if (this.isLocalDevelopment()) {
      const isDebug = level === LogLevel.DEBUG;
      const debugPrefix = isDebug ? '🔍 ' : '';

      switch (level) {
        case LogLevel.DEBUG:
          console.debug(`${debugPrefix}${baseMessage}`, ...rest);
          break;
        case LogLevel.INFO:
          console.info(baseMessage, ...rest);
          break;
        case LogLevel.WARN:
          console.warn(baseMessage, ...rest);
          break;
        case LogLevel.ERROR:
          console.error(baseMessage, ...rest);
          break;
        default:
          console.log(baseMessage, ...rest);
      }
    }
  }
}

// Exporte instância global
export const debugLogger = DebugLogger.getInstance();
